import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CopyOutlined } from "@ant-design/icons";
import ReactJson from "@microlink/react-json-view";
import Sidebar from "../../components/Sidebar";
import TopSummaryCard from "../../components/TopSummaryCard";
import { toggleFavorite, AnalysisInfo } from "../../lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { analysisKeys } from "../../lib/queries";
import { useAuth } from "../../context/AuthContext";
import { useSignInModal } from "../../context/SignInModalContext";
import IndicatorsOverviewsLayoutCard from "../../components/IndicatorsOverviewsLayoutCard";
import ObjectTreeStruct from "../../components/ObjectTreeStruct";
// import { getUrlSummaryTab } from "./AnalysisPageLayout.urltab";
import varistLogo from "../../assets/varist-navbar-logo.svg";
import { ReportLoadStatus } from "../../store";
import { ServerCategorizationsData } from "../../models/data-structures";
import { ReactComponent as ScrollUpIcon } from "../../assets/icons/arrow-up-circle.svg";
import AnalysisHeader from "./AnalysisHeader";
import ChatWidget from "./ChatWidget";

const customTheme = {
  base00: "unset",
  base01: "white",
  base02: "gray",
  base03: "white",
  base04: "gray",
  base05: "white",
  base06: "white",
  base07: "rgba(0, 207, 255, 0.56)",
  base08: "white",
  base09: "rgb(191, 208, 188)",
  base0A: "white",
  base0B: "rgb(255, 200, 0)",
  base0C: "rgb(0, 174, 255)",
  base0D: "gray",
  base0E: "gray",
  base0F: "rgb(255, 200, 0)",
  boolean: "boolean",
  null: "null",
};

function copyJSONToClipboard(json: string) {
  const textarea = document.createElement("textarea");
  textarea.value = json;
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function showTermsAndConditions() {
  const element = document.getElementById("cond");
  if (element) {
    window.scrollTo(0, 0);
    element.style.display = "block";
  }
}

const ScrollToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  return (
    <div
      className={`scroll-button ${isVisible ? "visible" : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <ScrollUpIcon />
    </div>
  );
};

interface AnalysisPageLayoutProps {
  analysisId: string;
  reportData: ServerCategorizationsData | null;
  analysisInfo: AnalysisInfo | null;
  loadStatus: ReportLoadStatus;
  errorMsg: string | null;
}

const AnalysisPageLayout: React.FC<AnalysisPageLayoutProps> = ({
  analysisId,
  reportData,
  analysisInfo,
  loadStatus,
  errorMsg,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = searchParams.get("tab") === "json" ? 0 : 1;
  // 0: indicators, 1: objects
  let subTab = 0;
  const viewParam = searchParams.get("view");
  if (viewParam === "objects") subTab = 1;

  const isMobile = window.innerWidth <= 650;
  const isLoaded = loadStatus === ReportLoadStatus.REPORT_LOADED;

  const setMainTab = (tab: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 0) next.set("tab", "json");
        else next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  const setSubTab = (tab: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 1) next.set("view", "objects");
        else next.delete("view");
        return next;
      },
      { replace: true },
    );
  };

  const { isAuthenticated } = useAuth();
  const { openSignInModal } = useSignInModal();
  const queryClient = useQueryClient();

  const isFavorite = analysisInfo?.is_favorite ?? false;

  const favoriteMutation = useMutation({
    mutationFn: (newIsFavorite: boolean) =>
      toggleFavorite(analysisId, newIsFavorite),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: analysisKeys.info(analysisId),
      });
    },
  });

  const handleFavoriteToggle = () => {
    if (!isAuthenticated) {
      openSignInModal();
      return;
    }
    favoriteMutation.mutate(!isFavorite);
  };

  return (
    <div className="main-vertical-divider active">
      <Sidebar
        mainTab={mainTab}
        setMainTab={setMainTab}
        reportLoadStatus={loadStatus}
      />
      <div className="main-content-area" id="main-content">
        <Link to="/" className="print-logo-link">
          <img
            className="print-logo"
            src={varistLogo}
            alt="Varist logo - print friendly"
          />
        </Link>
        <AnalysisHeader
          analysisId={analysisId}
          loadStatus={loadStatus}
          errorMsg={errorMsg}
          isFavorite={isFavorite}
          favoriteLoading={favoriteMutation.isPending}
          onFavoriteToggle={handleFavoriteToggle}
        />
        {isLoaded && reportData && mainTab === 1 && (
          <div id="report-output" className="file-dependent-content">
            <TopSummaryCard jsonData={reportData} />
            <div style={{ position: "relative", marginTop: "60px" }}>
              <div className="tabs">
                <div
                  id="ind"
                  className={subTab === 0 ? "active" : ""}
                  onClick={() => setSubTab(0)}
                >
                  Indicators
                </div>
                {!isMobile && (
                  <div
                    id="obj"
                    className={subTab === 1 ? "active" : ""}
                    onClick={() => setSubTab(1)}
                  >
                    Objects
                  </div>
                )}
              </div>
              <span
                id="indicators"
                style={{ display: subTab === 0 ? "block" : "none" }}
              >
                <IndicatorsOverviewsLayoutCard data={reportData} />
              </span>
              <span
                id="objects"
                style={{
                  display: isMobile ? "none" : subTab === 1 ? "block" : "none",
                }}
              >
                <ObjectTreeStruct data={reportData} />
              </span>
            </div>
            <div className="tc">
              By uploading a file you agree to the{" "}
              <button onClick={showTermsAndConditions}>Terms of Use</button>
            </div>
          </div>
        )}
        {mainTab === 0 && reportData && isLoaded && (
          <div
            id="JSON-output"
            style={{ margin: "40px 20px", fontSize: "16px" }}
          >
            <h2>
              <span
                className="icon-with-background clickable"
                onClick={() =>
                  copyJSONToClipboard(JSON.stringify(reportData, null, 2))
                }
              >
                <CopyOutlined />
              </span>{" "}
              JSON output
            </h2>
            <ReactJson
              src={reportData}
              name={false}
              theme={customTheme}
              collapsed={false}
              displayDataTypes={false}
              enableClipboard={false}
              indentWidth={2}
            />
            <ScrollToTopButton />
          </div>
        )}
        {isLoaded && <ChatWidget analysisId={analysisId} />}
      </div>
    </div>
  );
};

export default AnalysisPageLayout;
